import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/LanguageContext";
import GoogleLoginButton from "@/lib/GoogleLoginButton";

// Auth bridge for tenant hosts (see backend/auth_handoff.py).
// This page lives ONLY on the platform origin (platformRoutes — the non-tenant branch of
// App.js): it is the single origin registered with Google, hence the only one where GIS
// works. Flow: Google here → POST /auth/handoff/mint → redirect to
// {tenant origin}/api/auth/handoff/consume?code=… which sets the host-only cookie and
// sends the visitor back to the page they came from.
export default function AuthBridge() {
  const [params] = useSearchParams();
  const { user, loading, logout } = useAuth();
  const t = useT("saas");
  const [error, setError] = useState("");
  const [minting, setMinting] = useState(false);
  const [switching, setSwitching] = useState(false);

  const returnUrl = useMemo(() => {
    const raw = params.get("return") || "";
    try {
      const u = new URL(raw);
      if (u.protocol !== "http:" && u.protocol !== "https:") return null;
      return u;
    } catch {
      return null;
    }
  }, [params]);

  const mintAndGo = useCallback(async () => {
    if (!returnUrl || minting) return;
    setMinting(true);
    try {
      const { data } = await api.post("/auth/handoff/mint", { return_url: returnUrl.toString() });
      // Navigate to the CANONICAL url the server returned (built from the host it resolved
      // in the database). NEVER recompute it here from returnUrl.origin: Python and the
      // browser split the authority differently, and a "https://evil.com\@tenant/..."
      // validated server-side then pointed at the attacker, who collected the single-use
      // code (account takeover).
      if (!data?.consume_url) throw new Error("missing consume_url");
      window.location.assign(data.consume_url);
    } catch (e) {
      // 400 = host not allowlisted: NEVER redirect to the unvalidated url.
      setError(t("authBridge.mintFailed"));
      setMinting(false);
    }
  }, [returnUrl, minting, t]);

  // A platform session already here used to mint and leave AUTOMATICALLY. That is what made
  // the whole product impossible to sign into as somebody else: every tenant host bounces
  // through this page, so the second practitioner on a shared browser was handed the first
  // one's session without ever seeing Google. The choice is now explicit — continue as this
  // account, or drop the platform session and sign in again (the Google button below forces
  // the account chooser, cf. lib/GoogleLoginButton).
  const useAnotherAccount = useCallback(async () => {
    setSwitching(true);
    try {
      await logout();          // clears the platform session cookie AND `user`
    } finally {
      setSwitching(false);     // `user` is null now → the Google button renders
    }
  }, [logout]);

  const busy = loading || minting || switching;

  return (
    <div className="min-h-screen flex items-center justify-center bg-eh-bg px-6">
      <div
        data-testid="auth-bridge-card"
        className="max-w-md w-full bg-eh-cream rounded-3xl border border-eh-border-soft p-10 shadow-[0_24px_60px_-25px_rgba(44,53,49,0.2)] text-center"
      >
        <h1 className="font-display text-3xl text-eh-ink leading-tight">{t("authBridge.title")}</h1>
        {!returnUrl ? (
          <>
            <p className="text-eh-muted mt-3" data-testid="bridge-invalid-return">{t("authBridge.invalidReturn")}</p>
            <Link to="/" className="inline-block mt-6 text-sm text-eh-sage-deep hover:underline">{t("authBridge.back")}</Link>
          </>
        ) : error ? (
          <>
            <p className="text-red-600 mt-3" data-testid="bridge-error">{error}</p>
            <Link to="/" className="inline-block mt-6 text-sm text-eh-sage-deep hover:underline">{t("authBridge.back")}</Link>
          </>
        ) : busy ? (
          <div className="mt-8 flex items-center justify-center gap-2 text-eh-muted" data-testid="bridge-redirecting">
            <Loader2 className="animate-spin" size={18} /> {t("authBridge.redirecting")}
          </div>
        ) : user ? (
          <>
            <p className="text-eh-muted mt-3">{t("authBridge.sessionPrompt")}</p>
            <div className="mt-8 space-y-3" data-testid="bridge-account-choice">
              <button
                type="button"
                onClick={mintAndGo}
                data-testid="bridge-continue-as"
                className="w-full px-6 py-3 rounded-full bg-eh-ink text-eh-cream hover:bg-eh-ink/90 transition text-sm font-medium"
              >
                {t("authBridge.continueAs", { email: user.email })}
              </button>
              <button
                type="button"
                onClick={useAnotherAccount}
                data-testid="bridge-use-another"
                className="w-full px-6 py-3 rounded-full border border-eh-border-soft text-eh-ink hover:bg-eh-ink/5 transition text-sm font-medium"
              >
                {t("authBridge.useAnotherAccount")}
              </button>
            </div>
            <Link to="/" className="inline-block mt-6 text-sm text-eh-muted hover:underline">{t("authBridge.back")}</Link>
          </>
        ) : (
          <>
            <p className="text-eh-muted mt-3">{t("authBridge.prompt")}</p>
            <div className="mt-8" data-testid="bridge-google-signin">
              <GoogleLoginButton onSuccess={mintAndGo} />
            </div>
            <Link to="/" className="inline-block mt-6 text-sm text-eh-muted hover:underline">{t("authBridge.back")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
